import React, { useState } from 'react';
import { X, Copy, Mail, MessageSquare, Link, Download } from 'lucide-react';
import Button from './Button';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  documentId: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, documentName, documentId }) => {
  const [shareLink, setShareLink] = useState(`https://solutionai.com/documents/${documentId}`);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const handleEmailShare = () => {
    const subject = `Document partagé: ${documentName}`;
    const body = `Bonjour,\n\nJe vous partage ce document: ${documentName}\n\nLien: ${shareLink}\n\nMessage: ${message}`;
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink);
  };

  const handleDownloadLink = () => {
    // Créer un lien de téléchargement temporaire
    const link = document.createElement('a');
    link.href = shareLink;
    link.download = documentName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-eg-blanc rounded-md shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-eg-encre">Partager le document</h3>
          <button
            onClick={onClose}
            className="text-eg-estompe hover:text-eg-gris transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Document info */}
          <div className="bg-eg-papier rounded-md p-4">
            <h4 className="font-medium text-eg-encre">{documentName}</h4>
            <p className="text-sm text-eg-gris">ID: {documentId}</p>
          </div>

          {/* Share link */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-eg-brun">
              Lien de partage
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareLink}
                onChange={(e) => setShareLink(e.target.value)}
                className="flex-1 px-3 py-2 border border-[#DDE2DD] rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                readOnly
              />
              <Button
                onClick={handleCopyLink}
                variant={copied ? "primary" : "outline"}
                size="sm"
                className="flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copié!' : 'Copier'}
              </Button>
            </div>
          </div>

          {/* Email sharing */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-eg-brun">
              Partager par email
            </label>
            <div className="space-y-2">
              <input
                type="email"
                placeholder="Adresse email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <textarea
                placeholder="Message (optionnel)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <Button
                onClick={handleEmailShare}
                disabled={!email}
                className="w-full flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Envoyer par email
              </Button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleDownloadLink}
              variant="outline"
              className="flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Télécharger
            </Button>
            <Button
              onClick={() => {
                const whatsappLink = `https://wa.me/?text=${encodeURIComponent(`Regardez ce document: ${documentName} - ${shareLink}`)}`;
                window.open(whatsappLink, '_blank');
              }}
              variant="outline"
              className="flex items-center justify-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              WhatsApp
            </Button>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
